import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostBinding, OnInit, signal, ViewChild, inject, input, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter } from "rxjs/operators";

import { ConversationTypeEnum, SketchToolEnum } from "../../core-ui.enums";
import { IInteraction } from "../../models/IInteraction";
import {
  PointerService,
  SketchService,
  SnapshotService,
  StreamState,
  ThemeService,
} from "../../services";
import { ConferenceService } from "../../services/conference.service";
import { NotificationService } from "../../services/notification.service";
import { UserService } from "../../services/user.service";
import { AppConfigService } from "../../services/app.config.service";
import { AgentParam } from "../../models/application-options/AgentOptions";
import { ThemeParam } from "../../../core-ui/models";
import { TinyColor } from "@ctrl/tinycolor";

@Component({
  selector: "app-point-controls",
  templateUrl: "./point-controls.component.html",
  styleUrls: ["./point-controls.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PointControlsComponent implements OnInit {
  private sketchService = inject(SketchService);
  private pointerService = inject(PointerService);
  private snapshotService = inject(SnapshotService);
  private notification = inject(NotificationService);
  private conference = inject(ConferenceService);
  private user = inject(UserService);
  private config = inject(AppConfigService);
  private cd = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);

  readonly stream = input<StreamState>(undefined);
  readonly interaction = input<IInteraction>(undefined);
  readonly column = input<boolean>(undefined);

  readonly controlSelected = output<SketchToolEnum>();

  activeTool: SketchToolEnum = SketchToolEnum.pointer;
  isFrameFrozen = false;
  isZoomOn = false;

  color = signal(undefined);
  colors = signal<string[]>([]);
  assignedColors = signal<string[]>([]);
  isColorPickerOpen = signal(false);
  iconMode = signal("dark");

  @HostBinding("class") get class() {
    return {
      column: this.column(),
    };
  }

  ngOnInit(): void {
    this.colors.set(
      this.themeService.getOptions()[ThemeParam.AR_POINTER_COLORS]
    );
    this.sketchService.toolSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tool) => {
        this.activeTool = tool || SketchToolEnum.pointer;
        this.cd.detectChanges();
      });
    this.pointerService.freezeFrameChange$
      .pipe(
        filter(
          (e) => {
      const stream = this.stream();
      return e.target.endpoint === stream.originator.endpoint &&
              e.correlationId === stream.correlationId;
    }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        this.isFrameFrozen = e.on;
        // stop zoom if on
        if (!e.on && this.isZoomOn) {
          this.zoom();
        }
        // reset UI
        if (!e.on && this.activeTool === SketchToolEnum.magnify) {
          this.pointer();
        }
        this.cd.detectChanges();
      });
    this.pointerService.zoomFrameChange$
      .pipe(
        filter(
          (e) => {
      const stream = this.stream();
      return e.target.endpoint === stream.originator.endpoint &&
              e.correlationId === stream.correlationId;
    }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        this.isZoomOn = e.on;
        if (e.on) {
          this.activeTool = SketchToolEnum.magnify;
        }
        this.cd.detectChanges();
      });
    this.pointerService.pointColorsChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((colors) => {
        const color = colors[this.conference.myself.endpoint];
        this.assignedColors.set(Object.values(colors));
        this.color.set(color);
        const isLight = new TinyColor(color).isLight();
        this.iconMode.set(
          this.isPointerActive ? (isLight ? "dark" : "light") : "dark"
        );
        if (Object.values(colors).length === 0) {
          this.isColorPickerOpen.set(false);
        }
      });
  }

  snapshot() {
    const stream = this.stream();
    this.snapshotService.requestSnapshot(
      stream.originator,
      this.interaction()
    );
    // acquireSnapshot on snapshotService will also notify target of snapshot taken
    //  so if it's myself, don't notify twice
    if (stream.originator.username !== this.conference.myself.username) {
      this.notification.info("Click!", { body: "Snapshot taken" });
    }
  }

  freezeFrame() {
    this.pointerService.freezeFrame(
      this.stream().originator,
      this.stream().correlationId
    );
  }

  zoom() {
    const activateControl = this.isZoomOn
      ? SketchToolEnum.pointer
      : SketchToolEnum.magnify;
    this.sketchService.selectTool(activateControl);
    this.controlSelected.emit(activateControl);
    this.pointerService.zoomFrame(
      this.stream().originator,
      this.stream().correlationId
    );
  }

  pointer() {
    if (this.isZoomOn) {
      this.pointerService.zoomFrame(
        this.stream().originator,
        this.stream().correlationId
      );
    }
    this.sketchService.selectTool(SketchToolEnum.pointer);
    this.controlSelected.emit(SketchToolEnum.pointer);
  }

  changeColor(c: string) {
    if (this.isColorDisabled(c)) return;
    this.pointerService.claimColor(c, this.conference.myself);
    this.isColorPickerOpen.set(false);
  }

  isColorDisabled(color: string) {
    return this.assignedColors().includes(color);
  }

  get tooltipPosition() {
    return this.column() ? "left" : "bottom";
  }

  get isPointerActive() {
    return this.activeTool === SketchToolEnum.pointer;
  }

  get isSnapshotAvailable() {
    const stream = this.stream();
    return (
      stream.type !== "screen" &&
      this.user.isAgent &&
      this.config.agentParamEnabled(AgentParam.SNAPSHOT_ENABLED) &&
      this.snapshotService.supported(stream.originator) &&
      this.interaction().getType() !== ConversationTypeEnum.voiceCall
    );
  }

  get isFreezeFrameAvailable() {
    return this.stream().type !== "screen" && this.user.isAgent;
  }
}
